import { createContext, useCallback, useRef } from "react";
import { useDispatch } from "react-redux";
import { getAuth } from "firebase/auth";
import firebase from "firebase/compat/app";

import "firebase/compat/auth";
import "firebase/compat/database";
import "firebase/compat/firestore";

import {
  actionChannelRemove,
  actionChannelUpsert,
  actionDeleteChats,
  actionMessageAdd,
  actionMessageRemove,
} from "./redux/actions/chat";

// Initialize Firebase app
const fireApp = firebase.initializeApp({
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
});

const auth = firebase.auth();
const database = firebase.database(fireApp);
const authentication = getAuth(fireApp);

// Define the shape of the Firebase context
interface FirebaseContextType {
  startChats: (userId: string) => void;
  stopChats: () => void;
  sendChatMessage: (channelId: string, senderId: string, text: string) => void;
  setChatSeen: (channelId: string, timestamp: number) => void;
  deleteChat: (channelId: string, userId: string) => void;
}

// Create the Firebase context
const FirebaseContext = createContext<FirebaseContextType | null>(null);

// FirebaseProvider component
const FirebaseProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const dispatch = useDispatch();

  // Store Firebase subscriptions for cleanup
  const allChatSubscriptions = useRef<firebase.database.Reference[]>([]);

  // Start listening to chats for a specific user
  const startChats = useCallback(
    (userId: string) => {
      console.log("XXX Starting chats for userId:", userId);

      const channelIdsRef = database.ref("/chat/partyToChannelIds/" + userId);

      channelIdsRef.on(
        "child_added",
        (channelIdSnapshot: firebase.database.DataSnapshot) => {
          const channelId = channelIdSnapshot.key;

          if (!channelId) return;

          const channelsRef = database.ref("/chat/channels/" + channelId);

          channelsRef.on("value", (channelSnapshot: firebase.database.DataSnapshot) => {
            const channel = channelSnapshot.val();

            if (!channel) {
              console.warn("Got null channel snapshot for channelId:", channelId);
              return;
            }

            dispatch(actionChannelUpsert(channelId, channel));
          });

          const messagesRef = database.ref("/chat/messages/" + channelId);

          messagesRef.on("child_added", (messageSnapshot: firebase.database.DataSnapshot) => {
            dispatch(actionMessageAdd(channelId, messageSnapshot.val()));
          });

          messagesRef.on("child_removed", (messageSnapshot: firebase.database.DataSnapshot) => {
            dispatch(actionMessageRemove(channelId, messageSnapshot.val()));
          });

          allChatSubscriptions.current.push(channelsRef, messagesRef);
        },
        (err: Error) => {
          console.log("child_added got error", err);
        }
      );

      channelIdsRef.on("child_removed", (channelIdSnapshot: firebase.database.DataSnapshot) => {
        dispatch(actionChannelRemove(channelIdSnapshot.key!));
      });

      allChatSubscriptions.current.push(channelIdsRef);
    },
    [dispatch]
  );

  // Stop listening to chats and clean up subscriptions
  const stopChats = useCallback(() => {
    console.log("XXX Stopping chats");
    allChatSubscriptions.current.forEach((ref) => ref.off());
    dispatch(actionDeleteChats());
  }, [dispatch]);

  // Send a chat message
  const sendChatMessage = useCallback((channelId: string, senderId: string, text: string) => {
    const timestamp = new Date().getTime();
    const partyIds = b2cChannelIdToPartyIds(channelId);

    database.ref("/chat/partyToChannelIds/" + partyIds[0] + "/" + channelId).set(0);
    database.ref("/chat/partyToChannelIds/" + partyIds[1] + "/" + channelId).set(0);
    database.ref("/chat/messages/" + channelId).push({ timestamp, senderId, text });

    setChatSeen(channelId, timestamp);
  }, []);

  // Mark a chat as seen
  const setChatSeen = useCallback((channelId: string, timestamp: number) => {
    database.ref("/chat/channels/" + channelId + "/businessSeen").set(timestamp);
  }, []);

  // Delete a chat (tombstone it)
  const deleteChat = useCallback((channelId: string, userId: string) => {
    database.ref("/chat/channels/" + channelId + "/deleted").set({
      userId,
      time: new Date().getTime(),
    });
  }, []);

  // Expose Firebase functions via context
  const funcs: FirebaseContextType = {
    startChats,
    stopChats,
    sendChatMessage,
    setChatSeen,
    deleteChat,
  };

  return <FirebaseContext.Provider value={funcs}>{children}</FirebaseContext.Provider>;
};

// Helper function to extract party IDs from a channel ID
const b2cChannelIdToPartyIds = (channelId: string): string[] => {
  const parts = channelId.split(":");
  parts.shift();
  return parts;
};

export { auth, authentication, database, FirebaseContext, FirebaseProvider };
